import React, { useState } from "react";
import { Play } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const parseYouTube = (url) => {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
};
const parseVimeo = (url) => {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
};

const posterFor = (video) => {
  if (video.poster_url) return mediaUrl(video.poster_url);
  if (video.poster_image) return mediaUrl(video.poster_image);
  if (video.source_type === "youtube" && video.url) {
    const id = parseYouTube(video.url);
    if (id) return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
};

/** Click-to-load video facade. No iframe/video on page load; audio pauses on play. */
export const VideoFacade = ({ video, big = false }) => {
  const [loaded, setLoaded] = useState(false);
  const poster = posterFor(video);
  const vertical = video.aspect === "9:16";

  const start = () => {
    window.dispatchEvent(new CustomEvent("hns:video-play"));
    setLoaded(true);
  };

  const renderPlayer = () => {
    if (video.source_type === "youtube") {
      const id = parseYouTube(video.url);
      return (
        <iframe
          title={video.title}
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          allow="accelerated-sensors; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      );
    }
    if (video.source_type === "vimeo") {
      const id = parseVimeo(video.url);
      return (
        <iframe
          title={video.title}
          src={`https://player.vimeo.com/video/${id}?autoplay=1`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      );
    }
    // uploaded file
    return (
      <video
        src={mediaUrl(video.file_url || video.url)}
        poster={poster || undefined}
        controls
        playsInline
        autoPlay
        preload="none"
        className="absolute inset-0 w-full h-full object-contain bg-black"
        onPlay={() => window.dispatchEvent(new CustomEvent("hns:video-play"))}
      />
    );
  };

  return (
    <div className={`frame p-2 ${big ? "" : ""}`} style={{ backgroundColor: "var(--ink-raised)", ...(vertical ? { maxWidth: big ? 460 : 340, marginInline: "auto" } : {}) }} data-testid={`video-${video.id}`}>
      <div className="relative w-full" style={{ aspectRatio: vertical ? "9 / 16" : "16 / 9", background: "#000", borderRadius: "var(--radius)", overflow: "hidden" }}>
        {loaded ? (
          renderPlayer()
        ) : (
          <button
            onClick={start}
            className="group absolute inset-0 w-full h-full"
            aria-label={`Play ${video.title}`}
            data-testid={`video-play-${video.id}`}
          >
            {poster ? (
              <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
            ) : (
              <div className="absolute inset-0 grid place-items-center">
                <img src="/icon-512.png" alt="" className="w-24 opacity-40" />
              </div>
            )}
            <span className="absolute inset-0 grid place-items-center">
              <span className="grid place-items-center transition-transform group-hover:scale-110"
                    style={{ width: big ? 84 : 64, height: big ? 84 : 64, borderRadius: "50%", border: "1px solid var(--gold)", background: "rgba(11,9,8,.55)", color: "var(--gold)" }}>
                <Play size={big ? 30 : 22} fill="currentColor" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="px-2 pt-3 pb-1">
        <p className="font-caps text-gold" style={{ fontSize: big ? ".72rem" : ".62rem" }}>{video.title}</p>
        {video.caption && <p style={{ color: "var(--ash)", fontSize: ".8rem" }} className="mt-1">{video.caption}</p>}
      </div>
    </div>
  );
};
